import React, { useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { MapPin, Phone, CheckCircle2, ArrowRight, Info, HelpCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { getCityBySlug, CITIES } from "@/data/cities";
import { SERVICES } from "@/data/services";
import { getLocalContent } from "@/data/local_content";
import { useSeo, JsonLd } from "@/lib/seo";
import { trackEvent, setLeadContext, cityGroupFromValue } from "@/lib/analytics";

/**
 * Page locale paramétrée par prestation × ville.
 * URLs ne sont PAS exposées dans la navigation — référencement Google uniquement.
 *
 * Props:
 *  - serviceSlug: "photobooth" | "anniversaire" | "entreprise"
 *  - citySlug:    "lille" | "tourcoing" | ...
 */
export default function LocalServicePage({ serviceSlug, citySlug }) {
  const city = getCityBySlug(citySlug);
  const service = SERVICES[serviceSlug];
  const valid = !!(city && service);

  // Enriched per-city × per-service content (Lille V2 prototypes for now).
  // When absent, we fall back to the shared services.js template.
  const local = valid ? getLocalContent(citySlug, serviceSlug) : null;

  const title = valid
    ? (local?.seo_title || `${service.title_prefix} ${city.name} (${city.cp}) — My Animation`)
    : "";
  const description = valid
    ? (local?.seo_description || service.subtitle_template({ city }))
    : "";
  const path = valid ? `/${service.url_prefix}-${city.slug}` : "";
  const keywords = valid
    ? `${service.keyword_prefix} ${city.name}, ${service.keyword_prefix} ${city.dept}, ${service.label} ${city.name}`
    : "";

  useSeo({ title, description, path, keywords });

  // view_local_page — déclenché UNE SEULE FOIS au montage pour chaque (service, ville).
  // Protection anti-StrictMode via useRef.
  const viewTrackedRef = useRef(null);
  useEffect(() => {
    if (!valid) return;
    const key = `${service.slug}:${city.slug}`;
    if (viewTrackedRef.current === key) return;
    viewTrackedRef.current = key;
    trackEvent("view_local_page", {
      service_type: service.slug,
      page_type: "local_service",
      city: city.slug,
      city_group: cityGroupFromValue(city.name),
      source_page: path,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, service?.slug, city?.slug]);

  if (!valid) return <Navigate to="/" replace />;

  const trackCtx = {
    service_type: service.slug,
    page_type: "local_service",
    city: city.slug,
    source_page: path,
  };
  const onClickPhone = () => trackEvent("click_phone", trackCtx);
  const onClickQuote = () => {
    setLeadContext(trackCtx);
    trackEvent("click_quote", trackCtx);
  };
  const onViewService = (target) => {
    trackEvent("view_service", { ...trackCtx, target_page: target });
  };

  const Icon = service.icon;
  const neighborCities = (city.neighbors || [])
    .map((slug) => CITIES.find((c) => c.slug === slug))
    .filter(Boolean);
  const mainServicePath =
    serviceSlug === "photobooth" ? "/photobooth" :
    serviceSlug === "anniversaire" ? "/anniversaire" :
    serviceSlug === "entreprise" ? "/entreprises" : "/prestations";

  return (
    <>
      <JsonLd
        id={`jsonld-${service.slug}-${city.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `https://myanimation.fr${path}`,
          name: `My Animation — ${service.label}`,
          description: `${service.label} professionnel à ${city.name} (${city.dept}).`,
          url: `https://myanimation.fr${path}`,
          telephone: "+33769224956",
          email: "my-animation@outlook.fr",
          priceRange: "€€€",
          address: {
            "@type": "PostalAddress",
            addressRegion: "Hauts-de-France",
            addressCountry: "FR",
          },
          areaServed: {
            "@type": "City",
            name: city.name,
            address: {
              "@type": "PostalAddress",
              postalCode: city.cp,
              addressRegion: city.dept,
              addressCountry: "FR",
            },
          },
          serviceType: service.json_ld_service_type,
          sameAs: [
            "https://www.instagram.com/myanimationdj/",
            "https://www.facebook.com/p/Dj-Paps-MY-Animation-61574555792594/",
          ],
        }}
      />

      <PageHero
        eyebrow={service.eyebrow_template({ city })}
        title={
          <>
            {service.title_prefix} <span className="italic gold-text">{city.name}.</span>
          </>
        }
        subtitle={
          local ? (
            <>{local.hero_subtitle_line1} <span className="italic gold-text">{local.hero_subtitle_italic}</span></>
          ) : (
            service.subtitle_template({ city })
          )
        }
        img={service.hero_image}
      />

      {/* Intro contextualisée + tuiles */}
      <section className="py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <InfoTile
            icon={MapPin}
            label="Zone d'intervention"
            value={city.name}
            sub="Intervention depuis Bousbecque"
          />
          <InfoTile
            icon={Icon}
            label="Notre prestation"
            value={service.label}
            sub="Setup pro inclus"
          />
          <InfoTile
            icon={CheckCircle2}
            label="Devis"
            value={local ? "Rapide" : "Sous 24h"}
            sub={local ? "Proposition personnalisée" : "Personnalisé · sans engagement"}
          />
        </div>

        <div className="card-luxe p-8 md:p-12">
          <p className="eyebrow">L'expertise locale</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
            {service.label} à <span className="italic gold-text">{city.name}</span> : ce que nous apportons.
          </h2>
          <p className="text-white/75 mt-6 leading-relaxed">
            {local?.intro_paragraph || service.intro_template({ city })}
          </p>
          {local?.context_local && (
            <p className="text-white/70 mt-5 leading-relaxed">
              {local.context_local}
            </p>
          )}
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {service.features.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-white/75 leading-relaxed">
                <CheckCircle2 size={16} strokeWidth={1.4} className="text-[#DCB457] shrink-0 mt-0.5" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* ===== Enriched sections (only when local content is defined) ===== */}
        {local?.event_types && (
          <div className="mt-10 card-luxe p-8 md:p-10">
            <p className="eyebrow">Formats fréquents</p>
            <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">{local.event_types_title}</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
              {local.event_types.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-white/75 leading-relaxed">
                  <span className="w-1 h-1 mt-2 bg-[#DCB457] shrink-0 rounded-full" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {local?.constraints && (
          <div className="mt-8 card-luxe p-8 md:p-10">
            <p className="eyebrow">
              <Info size={11} strokeWidth={1.7} className="inline text-[#DCB457] mr-1.5" />
              À anticiper
            </p>
            <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">{local.constraints_title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {local.constraints.map((c) => (
                <div key={c.title} className="border-l-2 border-[#DCB457]/40 pl-4">
                  <p className="text-[#DCB457] font-serif-luxe text-base">{c.title}</p>
                  <p className="text-sm text-white/65 mt-1.5 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {local?.billing_note && (
          <div className="mt-8 card-luxe p-8 md:p-10 border-[#DCB457]/25">
            <p className="eyebrow">Facturation & administratif</p>
            <p className="text-white/75 mt-4 leading-relaxed">{local.billing_note}</p>
          </div>
        )}
      </section>

      {/* Lieux emblématiques */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <p className="eyebrow">Lieux de réception connus dans le secteur</p>
        <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
          Exemples de lieux événementiels à <span className="italic gold-text">{city.name}</span> et dans les environs.
        </h2>
        <p className="text-white/65 text-sm mt-3 max-w-2xl">
          {local?.venues_note || `Parmi les lieux de ${city.name} et des communes voisines connus pour accueillir des événements. Vous avez un autre lieu en tête (salle privée, restaurant, bureau, à domicile) ? Nous nous adaptons à toutes les configurations.`}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {city.venues.map((v) => (
            <div key={v.name} className="card-luxe p-6">
              <h3 className="font-serif-luxe text-xl text-[#DCB457]">{v.name}</h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Enriched FAQ (only when local content defines one) */}
      {local?.faq && (
        <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
          <p className="eyebrow">
            <HelpCircle size={11} strokeWidth={1.7} className="inline text-[#DCB457] mr-1.5" />
            Questions fréquentes
          </p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">{local.faq_title}</h2>
          <div className="mt-8 space-y-4">
            {local.faq.map((item, i) => (
              <details
                key={i}
                className="group card-luxe p-6 open:border-[#DCB457]/45 transition-colors"
                data-testid={`local-faq-${service.slug}-${city.slug}-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-serif-luxe text-base md:text-lg text-white pr-4">{item.q}</span>
                  <span className="text-[#DCB457] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-white/70 mt-4 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
          {/* JSON-LD FAQPage for rich results */}
          <JsonLd
            id={`jsonld-faq-${service.slug}-${city.slug}`}
            data={{
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: local.faq.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            }}
          />
        </section>
      )}

      {/* Retours clients — remplace le témoignage inventé */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-8 md:p-12 text-center">
          <p className="eyebrow">Retours clients</p>
          <p className="text-white/75 text-base md:text-lg leading-relaxed mt-5">
            Vous avez organisé votre événement à {city.name} avec My Animation ?
            Votre retour pourra être ajouté ici avec votre autorisation.
          </p>
          <Link
            to="/avis"
            data-testid={`local-avis-link-${service.slug}-${city.slug}`}
            className="inline-flex items-center gap-2 text-[#DCB457] hover:underline text-sm mt-5"
          >
            Voir tous les avis clients <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Forfaits */}
      {serviceSlug === "photobooth" ? (
        <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
          <div className="card-luxe p-8 md:p-12">
            <p className="eyebrow">Forfaits photobooth</p>
            <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
              Nos forfaits photobooth <span className="italic gold-text">à partir de 179 € TTC.</span>
            </h2>
            <p className="text-white/75 mt-6 leading-relaxed max-w-3xl">
              Chaque événement est unique. Le tarif dépend notamment de la durée, du lieu,
              des options d'impression et de personnalisation choisies. Découvrez le détail
              de nos forfaits photobooth ou demandez une proposition personnalisée pour votre
              événement à <span className="text-white">{city.name}</span>.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link
                to="/photobooth"
                data-testid={`local-forfaits-link-${service.slug}-${city.slug}`}
                onClick={() => onViewService("/photobooth")}
                className="btn-gold inline-flex items-center gap-2"
              >
                Voir les forfaits photobooth <ArrowRight size={14} strokeWidth={1.4} />
              </Link>
              <Link
                to="/contact"
                data-testid={`local-forfaits-devis-${service.slug}-${city.slug}`}
                onClick={onClickQuote}
                className="btn-ghost-gold inline-flex items-center gap-2"
              >
                Demander un devis personnalisé <ArrowRight size={14} strokeWidth={1.4} />
              </Link>
            </div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-6">
              Tarifs TTC · Proposition sur-mesure selon votre lieu, durée et options.
            </p>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
          <p className="eyebrow">Forfaits {service.label.toLowerCase()}</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
            Nos formules pour <span className="italic gold-text">{city.name}</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
            {service.packages.map((pack) => (
              <PackTile key={pack.name} {...pack} />
            ))}
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-6 text-center">
            Tarifs TTC · Devis personnalisé selon votre lieu, durée et options.
          </p>
        </section>
      )}

      {/* Maillage interne : prestations + villes voisines + FAQ + avis + contact */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto border-t border-[#DCB457]/12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="eyebrow">Autres prestations utiles</p>
            <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">Compléter votre événement</h3>
            <p className="text-white/65 text-sm mt-3 leading-relaxed">
              La prestation {service.label.toLowerCase()} peut être combinée avec d'autres services :
              DJ, photobooth, animation. Retrouvez également nos avis clients et
              les questions les plus fréquentes.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to={mainServicePath} data-testid={`local-link-main-${service.slug}-${city.slug}`} onClick={() => onViewService(mainServicePath)} className="btn-ghost-gold text-xs">{service.label} — la prestation</Link>
              {serviceSlug !== "photobooth" && (
                <Link to="/photobooth" data-testid={`local-link-photobooth-${service.slug}-${city.slug}`} onClick={() => onViewService("/photobooth")} className="btn-ghost-gold text-xs">Photobooth</Link>
              )}
              <Link to="/avis" data-testid={`local-link-avis-${service.slug}-${city.slug}`} className="btn-ghost-gold text-xs">Avis clients</Link>
              <Link to="/faq" data-testid={`local-link-faq-${service.slug}-${city.slug}`} className="btn-ghost-gold text-xs">FAQ</Link>
              <Link to="/contact" data-testid={`local-link-contact-${service.slug}-${city.slug}`} onClick={onClickQuote} className="btn-ghost-gold text-xs">Contact</Link>
            </div>
          </div>

          {neighborCities.length > 0 && (
            <div>
              <p className="eyebrow">Villes voisines</p>
              <h3 className="font-serif-luxe text-2xl md:text-3xl mt-3">Autres villes desservies</h3>
              <p className="text-white/65 text-sm mt-3 leading-relaxed">
                My Animation intervient également à proximité de {city.name} :
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
                {neighborCities.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/${service.url_prefix}-${n.slug}`}
                    data-testid={`local-neighbor-${service.slug}-${city.slug}-${n.slug}`}
                    className="border border-[#DCB457]/20 hover:border-[#DCB457]/60 hover:bg-[#DCB457]/5 px-3 py-2.5 text-sm transition-colors"
                  >
                    <span className="block text-[10px] uppercase tracking-[0.16em] text-white/45">{n.cp}</span>
                    <span className="block text-white mt-0.5">{n.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-10 md:p-14 text-center">
          <p className="eyebrow">Devis personnalisé</p>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-4">
            Parlons de votre projet à<br />
            <span className="italic gold-text">{city.name}.</span>
          </h2>
          <p className="text-white/70 mt-5 max-w-md mx-auto">
            {local?.cta_final || "Devis personnalisé, sans engagement. Nous revenons vers vous rapidement avec une proposition adaptée."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link
              to="/contact"
              data-testid={`local-cta-devis-${service.slug}-${city.slug}`}
              onClick={onClickQuote}
              className="btn-gold inline-flex items-center gap-2"
            >
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a
              href="tel:0769224956"
              data-testid={`local-cta-tel-${service.slug}-${city.slug}`}
              onClick={onClickPhone}
              className="btn-ghost-gold inline-flex items-center gap-2"
            >
              <Phone size={14} strokeWidth={1.4} /> 07 69 22 49 56
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-luxe p-5 text-center">
      <Icon size={20} strokeWidth={1.3} className="text-[#DCB457] mx-auto" />
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/55 mt-3">{label}</p>
      <p className="font-serif-luxe text-2xl gold-text mt-2">{value}</p>
      <p className="text-[11px] text-white/40 mt-1">{sub}</p>
    </div>
  );
}

function PackTile({ name, price, desc, featured }) {
  return (
    <div className={`card-luxe p-7 ${featured ? "border-[#DCB457]/45" : ""}`}>
      {featured && (
        <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1 mb-3">
          Le plus choisi
        </span>
      )}
      <p className="eyebrow">{name}</p>
      <p className="font-serif-luxe text-3xl mt-3 gold-text">{price}</p>
      <p className="text-sm text-white/65 mt-4 leading-relaxed">{desc}</p>
    </div>
  );
}
